import React, { useEffect } from "react";
import { Drawer, Form, InputNumber, Space, ColorPicker, message } from "antd";
import { Button } from "reactstrap";

export interface IndicatorItem {
  title: string;
  color: string;
  startPrice: number;
  endPrice: number;
}

interface StatusIndicatorDrawerProps {
  visible: boolean;
  onClose: () => void;
  onSubmit: (indicators: IndicatorItem[]) => void;
  initialValues?: IndicatorItem[];
  loading?: boolean;
}

const indicatorConfig = [
  { key: "slightlyHigh", title: "Slightly High Range", color: "#f2d1ae" },
  { key: "high", title: "High Range", color: "#f4e065" },
  { key: "veryHigh", title: "Very High Range", color: "#faad14" },
  { key: "outlier", title: "Outlier Range", color: "#ee545b" },
];

const StatusIndicatorDrawer: React.FC<StatusIndicatorDrawerProps> = ({
  visible,
  onClose,
  onSubmit,
  initialValues,
  loading,
}) => {
  const [form] = Form.useForm();

  useEffect(() => {
    if (visible) {
      if (initialValues && initialValues.length === 4) {
        form.setFieldsValue({
          slightlyHigh: { from: initialValues[0].startPrice || undefined, to: initialValues[0].endPrice || undefined, color: initialValues[0].color },
          high: { from: initialValues[1].startPrice || undefined, to: initialValues[1].endPrice || undefined, color: initialValues[1].color },
          veryHigh: { from: initialValues[2].startPrice || undefined, to: initialValues[2].endPrice || undefined, color: initialValues[2].color },
          outlier: { from: initialValues[3].startPrice || undefined, color: initialValues[3].color },
        });
      } else {
        form.setFieldsValue({
          slightlyHigh: { color: "#f2d1ae" },
          high: { color: "#f4e065" },
          veryHigh: { color: "#faad14" },
          outlier: { color: "#ee545b" },
        });
      }
    }
  }, [visible, initialValues, form]);

  const resolveColor = (color: any, fallback: string): string => {
    if (!color) return fallback;
    if (typeof color === "string") return color;
    return color?.toHexString?.() || fallback;
  };

  const onFinish = (values: any) => {
    const indicators: IndicatorItem[] = [
      {
        title: "Slightly High",
        color: resolveColor(values.slightlyHigh?.color, "#f2d1ae"),
        startPrice: Number(values.slightlyHigh?.from || 0),
        endPrice: Number(values.slightlyHigh?.to || 0),
      },
      {
        title: "High",
        color: resolveColor(values.high?.color, "#f4e065"),
        startPrice: Number(values.high?.from || 0),
        endPrice: Number(values.high?.to || 0),
      },
      {
        title: "Very High",
        color: resolveColor(values.veryHigh?.color, "#faad14"),
        startPrice: Number(values.veryHigh?.from || 0),
        endPrice: Number(values.veryHigh?.to || 0),
      },
      {
        title: "Outlier",
        color: resolveColor(values.outlier?.color, "#ee545b"),
        startPrice: Number(values.outlier?.from || 0),
        endPrice: 0,
      },
    ];

    // Validate ranges are in order
    if (indicators[0].startPrice >= indicators[0].endPrice && indicators[0].endPrice > 0) {
      message.error("Slightly High: 'From' must be less than 'To'");
      return;
    }
    if (indicators[1].startPrice >= indicators[1].endPrice && indicators[1].endPrice > 0) {
      message.error("High: 'From' must be less than 'To'");
      return;
    }
    if (indicators[2].startPrice >= indicators[2].endPrice && indicators[2].endPrice > 0) {
      message.error("Very High: 'From' must be less than 'To'");
      return;
    }

    onSubmit(indicators);
  };

  const onReset = () => {
    form.resetFields();
    form.setFieldsValue({
      slightlyHigh: { color: "#f2d1ae" },
      high: { color: "#f4e065" },
      veryHigh: { color: "#faad14" },
      outlier: { color: "#ee545b" },
    });
  };

  return (
    <Drawer
      title="Set Comparison Indicators"
      width={360}
      onClose={onClose}
      open={visible}
      bodyStyle={{ paddingBottom: 80 }}
    >
      <Form form={form} layout="vertical" onFinish={onFinish}>
        {indicatorConfig.map((indicator) => (
          <Form.Item
            key={indicator.key}
            label={
              <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <Form.Item name={[indicator.key, "color"]} noStyle>
                  <ColorPicker size="small" format="hex" />
                </Form.Item>
                {indicator.title}
              </span>
            }
            required
            style={{ marginBottom: 16 }}
          >
            <Space>
              <Form.Item
                name={[indicator.key, "from"]}
                noStyle
                rules={[{ required: true, message: "Required" }]}
              >
                <InputNumber placeholder="From %" min={0} style={{ width: 150 }} />
              </Form.Item>
              {indicator.key !== "outlier" && (
                <Form.Item
                  name={[indicator.key, "to"]}
                  noStyle
                  rules={[{ required: true, message: "Required" }]}
                >
                  <InputNumber placeholder="To %" min={0} style={{ width: 150 }} />
                </Form.Item>
              )}
            </Space>
          </Form.Item>
        ))}

        <Form.Item>
          <Space>
            <Button color="primary" type="submit" disabled={loading}>
              {loading ? "Saving..." : "Save"}
            </Button>
            <Button color="secondary" onClick={onReset}>
              Reset
            </Button>
          </Space>
        </Form.Item>
      </Form>
    </Drawer>
  );
};

export default StatusIndicatorDrawer;
